import { toast } from 'react-toastify';
import { showToast } from '@/utils/showToast';
import { BrokerRequest, DesiredContactTime, FormOfAddress, IHousingObjectResponse } from '@/types';
import { buildObjectInquiryPayload, pushObjectInquiry } from '@/lib/analytics';
import { useCallback, useMemo, useState } from 'react';
import { FORM_INITIAL_VALUES, validationSchema } from '../constants';
import { useTranslations } from 'next-intl';
import { useAuth } from '@/contexts';
import { normalizeContactFormValues } from '@/common';
import { useCreateInquiry, useGetLastBrokerRequest, useGetWhatsappMessageStatus } from '@/queris';
import { useIsMounted } from '@/hooks';

const LOCAL_STORAGE_KEY = 'quickReqForm';

const getLocalStorageFormData = (): Partial<BrokerRequest> | null => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Partial<BrokerRequest>;
  } catch {
    console.log('Error getting local storage form data');
    return null;
  }
};

const saveLocalStorageFormData = (values: BrokerRequest): void => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(values));
  } catch {
    console.log('Error saving local storage form data');
  }
};

const normalizeEnumDisplayValue = <T extends Record<string, string>>(
  enumObj: T,
  raw: unknown,
): string => {
  if (typeof raw !== 'string') return '';
  if (raw in enumObj) return enumObj[raw as keyof T];
  if (Object.values(enumObj).includes(raw)) return raw;

  return '';
};

export const useSendRequestForm = ({
  objectId,
  onComplete,
  listingData,
}: {
  objectId: string;
  onComplete?: () => void;
  listingData?: IHousingObjectResponse | null;
}) => {
  const t = useTranslations();
  const { isAuthenticated, user } = useAuth();

  const [cooldown, setCooldown] = useState(false);
  const [whatsappUnavailable, setWhatsappUnavailable] = useState(false);
  const isMounted = useIsMounted();

  const { data: lastRequestData, isLoading: isLastRequestLoading } = useGetLastBrokerRequest(
    isMounted && isAuthenticated,
  );

  const { mutate: checkWhatsappStatus, isPending: isCheckWhatsappStatusPending } =
    useGetWhatsappMessageStatus(
      () => {
        showToast.success(t('housingCardDetails.form.whatsappSent') as string);
      },
      () => toast.error(t('housingCardDetails.form.whatsappFailed') as string),
    );

  const { mutate: createInquiry, isPending } = useCreateInquiry(
    (whatsappMessageId, inquiryObj) => {
      const sendWhatsapp = inquiryObj.sendWhatsapp;
      if (listingData) {
        if (!sendWhatsapp) {
          pushObjectInquiry(buildObjectInquiryPayload(listingData, objectId, 'Form'));
        } else if (whatsappMessageId) {
          pushObjectInquiry(buildObjectInquiryPayload(listingData, objectId, 'WhatsApp'));
        }
      }
      if (sendWhatsapp) {
        if (!whatsappMessageId) {
          toast.error(t('housingCardDetails.form.whatsappFailed') as string);
        } else {
          toast.info(t('housingCardDetails.form.whatsappRequestSent') as string);
          onComplete?.();
          checkWhatsappStatus({ id: whatsappMessageId });
        }
      } else {
        showToast.success(t('housingCardDetails.form.emailSent') as string);
        onComplete?.();
      }
    },
    (statusCode, { sendWhatsapp }) => {
      if (sendWhatsapp && statusCode === 400) {
        toast.error(t('housingCardDetails.form.whatsappNotAvailable') as string);
        setWhatsappUnavailable(true);
      } else {
        toast.error(t('housingCardDetails.form.emailSendError') as string);
      }
    },
  );

  const triggerCooldown = useCallback(() => {
    setCooldown(true);
    setTimeout(() => setCooldown(false), 10000);
  }, []);

  const initialValues: BrokerRequest = useMemo(() => {
    if (!isMounted) {
      return { ...FORM_INITIAL_VALUES } as BrokerRequest;
    }

    if (lastRequestData) {
      return {
        ...FORM_INITIAL_VALUES,
        ...lastRequestData,
        formOfAddress: normalizeEnumDisplayValue(FormOfAddress, lastRequestData.formOfAddress),
        desiredContactTime: normalizeEnumDisplayValue(
          DesiredContactTime,
          lastRequestData.desiredContactTime,
        ),
      };
    }

    if (isAuthenticated) {
      return {
        ...FORM_INITIAL_VALUES,
        name: user?.given_name || '',
        surname: user?.family_name || '',
        email: user?.email || '',
        phone: user?.phone || '',
      } as BrokerRequest;
    }

    const localData = getLocalStorageFormData();
    if (localData) {
      return {
        ...FORM_INITIAL_VALUES,
        ...localData,
        formOfAddress: normalizeEnumDisplayValue(FormOfAddress, localData.formOfAddress),
        desiredContactTime: normalizeEnumDisplayValue(
          DesiredContactTime,
          localData.desiredContactTime,
        ),
      } as BrokerRequest;
    }

    return { ...FORM_INITIAL_VALUES } as BrokerRequest;
  }, [lastRequestData, user, isAuthenticated, isMounted]);

  const handleSubmit = async (values: BrokerRequest, sendWhatsapp: boolean) => {
    const inquiryObj = normalizeContactFormValues({
      ...values,
      sendWhatsapp,
    });

    if (objectId) {
      if (!isAuthenticated) {
        saveLocalStorageFormData(values);
      }
      createInquiry({ objectId, inquiryObj });
      triggerCooldown();
    }
  };

  return {
    initialValues,
    validationSchema: validationSchema(t),
    handleSubmit,
    isDisabled: isMounted && (isPending || isLastRequestLoading || isCheckWhatsappStatusPending),
    isPending,
    isCheckWhatsappStatusPending,
    isLastRequestLoading,
    triggerCooldown,
    cooldown,
    whatsappUnavailable,
  };
};
